import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";
import { useTranslation } from "react-i18next";
import { Button, VirtualTable, Spinner, SelectField, AnimatedDialog } from "@/components/reusable";
import type { ParsedWorksheet } from "@/types";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/utils";

interface FilePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileName: string;
  schemaName: string;
  worksheets: ParsedWorksheet[];
  onConfirm: () => void;
  onCancel: () => void;
  /** When true, the confirm button shows a loading spinner and cancel is disabled */
  isUploading?: boolean;

  // ── Intelligent File Classification ──
  /** When true, the File Type dropdown is shown and Confirm is gated on a selected type. */
  requiresReportType?: boolean;
  // The File Type dropdown sits between the Target schema line and the
  // subtitle. These props are populated by the parallel classifyFile
  // call on the Upload page; until allowedReportTypes lands we show a
  // spinner and gate the Confirm button behind a non-null reportType.
  /** The user-confirmed report type (e.g. "claims"). Null means the user hasn't picked yet. */
  reportType?: string | null;
  /** Raw detected_type from the backend cascade (may be "unknown"). */
  detectedType?: string | null;
  /** Allowed values for the File Type dropdown — from the classification registry. */
  allowedReportTypes?: string[];
  /** True while POST /files/classify is in flight — renders the spinner. */
  isClassifying?: boolean;
  /** Called when the user picks a new value in the dropdown. */
  onReportTypeChange?: (value: string | null) => void;
}

export function FilePreviewDialog({
  open,
  onOpenChange,
  fileName,
  schemaName,
  worksheets,
  onConfirm,
  onCancel,
  isUploading = false,
  requiresReportType = false,
  reportType = null,
  detectedType = null,
  allowedReportTypes = [],
  isClassifying = false,
  onReportTypeChange,
}: FilePreviewDialogProps) {
  const { t } = useTranslation();

  // Build the dropdown options from the server-supplied allowed list.
  // Labels are i18n-keyed under upload.fileType.<report_type> so new
  // schemas only need new translations — no code changes in the UI.
  // Unknown report_type values fall back to a title-cased version of
  // the raw string so we stay resilient to registry additions.
  const fileTypeOptions = allowedReportTypes.map((rt) => {
    const label = t(`upload.fileType.${rt}`, {
      defaultValue: rt.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    });
    return { value: rt, label };
  });

  // A reportType value is required before the user can proceed. The
  // classifier may have seeded this from detectedType; if it couldn't
  // (unknown / error / still classifying) the user must pick manually.
  const hasConfirmedType = !!reportType && reportType !== "unknown";
  const showRequiredWarning =
    requiresReportType && !isClassifying && !hasConfirmedType && detectedType === "unknown";
  const confirmDisabled = requiresReportType
    ? isUploading || isClassifying || !hasConfirmedType
    : isUploading;

  return (
    <AnimatedDialog
      open={open}
      onOpenChange={isUploading ? () => {} : onOpenChange}
      maxWidth="max-w-4xl"
      className="p-6 max-h-[85vh] overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex items-baseline justify-between mb-1">
        <Dialog.Title className="text-lg font-semibold text-[var(--color-text-primary)]">
          {t("upload.previewTitle")}
          <span className="ml-2 text-sm font-normal text-[var(--color-text-tertiary)]">
            – {fileName}
          </span>
        </Dialog.Title>
        <Dialog.Description className="sr-only">
          Preview uploaded file data before confirming
        </Dialog.Description>
        <span className="text-xs text-[var(--color-text-tertiary)]">
          uploaded {formatDateTime(new Date())}
        </span>
      </div>

      <div className="mb-4 space-y-2">
        <p className="text-sm text-[var(--color-text-secondary)]">
          {t("upload.targetSchema")}:{" "}
          <span className="font-medium text-[var(--color-brand-primary)] underline underline-offset-2">
            {schemaName}
          </span>
        </p>

        {requiresReportType && (
          <>
            {/* File Type classification dropdown. Rendered between the target
                schema line and the subtitle so the user's eye lands on it as
                the most important input on the Preview screen. */}
            <div className="flex items-center gap-3 pt-1">
              <label className="text-sm font-semibold text-[var(--color-text-primary)] whitespace-nowrap">
                {t("upload.fileTypeLabel")}:
              </label>
              <div className="flex-1 max-w-xs" data-testid="file-type-select">
                <SelectField
                  value={reportType ?? ""}
                  onValueChange={(v) => onReportTypeChange?.(v || null)}
                  options={fileTypeOptions}
                  placeholder={
                    isClassifying
                      ? t("upload.fileTypeClassifying")
                      : t("upload.fileTypePlaceholder")
                  }
                  disabled={isClassifying || fileTypeOptions.length === 0 || isUploading}
                />
              </div>
              {isClassifying && (
                <span
                  className="flex items-center gap-1.5 text-xs text-[var(--color-text-tertiary)]"
                  aria-live="polite"
                >
                  <Spinner size="sm" />
                  {t("upload.fileTypeClassifying")}
                </span>
              )}
            </div>
          </>
        )}

        {/* Blocking validation message when classification returned
            'unknown' and the user hasn't overridden it yet. */}
        {showRequiredWarning && (
          <p className="text-xs text-[var(--color-status-error, #b91c1c)]">
            {t("upload.fileTypeRequired")}
          </p>
        )}

        <p className="text-xs text-[var(--color-text-tertiary)]">{t("upload.previewSubtitle")}</p>
      </div>

      {/* Worksheet Tabs + Table */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {worksheets.length > 1 ? (
          <Tabs.Root defaultValue={worksheets[0]?.name}>
            <Tabs.List className="flex gap-1 border-b border-[var(--color-border-light)] mb-3">
              {worksheets.map((ws) => (
                <Tabs.Trigger
                  key={ws.name}
                  value={ws.name}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors border-b-2 flex items-center gap-1.5",
                    "data-[state=active]:border-[var(--color-brand-primary)] data-[state=active]:text-[var(--color-brand-primary)]",
                    "data-[state=inactive]:border-transparent data-[state=inactive]:text-[var(--color-text-tertiary)] data-[state=inactive]:hover:text-[var(--color-text-secondary)]"
                  )}
                >
                  {ws.name}
                  {ws.isMetadata && (
                    <span className="px-1 py-0.5 text-[10px] rounded bg-[var(--color-bg-tertiary)] text-[var(--color-text-tertiary)] font-normal">
                      meta
                    </span>
                  )}
                </Tabs.Trigger>
              ))}
            </Tabs.List>
            {worksheets.map((ws) => (
              <Tabs.Content key={ws.name} value={ws.name}>
                <VirtualTable
                  headers={ws.headers}
                  rows={ws.rows}
                  totalRows={ws.totalRows}
                  maxHeight="calc(80vh - 310px)"
                />
              </Tabs.Content>
            ))}
          </Tabs.Root>
        ) : worksheets[0] ? (
          <VirtualTable
            headers={worksheets[0].headers}
            rows={worksheets[0].rows}
            totalRows={worksheets[0].totalRows}
            maxHeight="calc(85vh - 260px)"
          />
        ) : null}
      </div>

      {/* Footer */}
      <div className="mt-5 shrink-0 text-center space-y-3">
        <p className="text-sm font-medium text-[var(--color-text-secondary)]">
          {isUploading
            ? "Uploading file to the platform..."
            : requiresReportType && !hasConfirmedType
              ? t("upload.fileTypeRequired")
              : t("upload.confirmQuestion")}
        </p>
        <div className="flex justify-center gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={isUploading}>
            {t("upload.cancelUpload")}
          </Button>
          <Button onClick={onConfirm} disabled={confirmDisabled} data-testid="confirm-upload-btn">
            {isUploading ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" />
                Uploading...
              </span>
            ) : (
              t("upload.confirmUpload")
            )}
          </Button>
        </div>
      </div>
    </AnimatedDialog>
  );
}
